/* This example requires Tailwind CSS v2.0+ */
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { classNames, formatPrice, generateSlug } from "../utils/formatters";
import { inputClass } from "../utils/classes";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import "react-phone-number-input/style.css";
import BrandSelect from "../components/BrandSelect";
import YearSelect from "../components/YearSelect";
import InsuranceCompanySelect from "../components/InsuranceCompanySelect";
import InsuranceFrequencySelect from "../components/InsuranceFrequencySelect";
import Link from "next/link";
import Image from "next/image";
import { CURRENCY_KEYS } from "../utils/constants";

export default function Pricing() {
  const methods = useForm();
  const { register, handleSubmit, watch, formState, control, setValue } =
    methods;
  const { errors } = formState;

  const [status, setStatus] = useState<string | null>(null);
  const [requestError, setError] = useState<string[]>([]);
  const [suggestedPrice, setSuggestedPrice] = useState<number | null>(null);
  const saving = status === "processing";
  const success = status === "success";

  const brand = watch("brand");
  const model = watch("model");
  const year = watch("year");

  async function getCarValue() {
    try {
      const response = await fetch(
        `/api/car-value/${generateSlug(brand.name)}_${generateSlug(
          model
        )}_${year}`,
        {
          method: "GET",
          mode: "cors",
          cache: "no-cache",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
          },
          redirect: "follow",
          referrerPolicy: "no-referrer",
        }
      );

      const parsedRes = await response.json();
      setSuggestedPrice(Math.round(parsedRes.avgPriceCRC / 100) * 100);
      if (response.status === 200 && !parsedRes.errors?.length) {
      } else {
        setError(parsedRes.errors);
      }

      return parsedRes;
    } catch (e) {
      // setStatus("error");
    }
  }

  function setCarValue() {
    setValue("price", suggestedPrice);
  }

  useEffect(() => {
    if (brand?.name && model && year) {
      getCarValue();
    }
  }, [brand, model, year]);

  async function onSubmit(data) {
    const {
      firstName,
      lastName,
      primaryPhone,
      idNumber,
      email,
      brand,
      model,
      year,
      price,
      insuranceCompany,
      frequency,
      licensePlate,
    } = data;

    const parsedData = {
      contact: {
        name: firstName,
        last_name: lastName,
        phone: primaryPhone,
        cedula: idNumber,
        email,
        vehicle_brand: brand.name.toLowerCase(),
        vehicle_model: model,
        vehicle_year: year,
        vehicle_price: price,
        insurance_company: insuranceCompany,
        frequency,
        license_plate: licensePlate,
        status: "Nuevo",
      },
    };
    setStatus("processing");
    try {
      const response = await fetch("/api/insurance/somit", {
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
        },
        redirect: "follow",
        referrerPolicy: "no-referrer",
        body: JSON.stringify(parsedData),
      });

      const parsedRes = await response.json();
      if (
        // HTTP_ERROR_CODES.includes(response.status) ||
        // HTTP_ERROR_CODES.includes(parsedRes.status)
        response.status === 200 &&
        !parsedRes.errors?.length
      ) {
        setStatus("success");
        window.scrollTo(0, 0);
      } else {
        setStatus("error");
        setError(parsedRes.errors);
      }

      return parsedRes;
    } catch (e) {
      setStatus("error");
    }
  }

  return (
    <>
      <SEO title={``} siteTitle="Movi Autos" />
      <div className="bg-white">
        <div className="max-w-7xl mx-auto sm:p-4">
          <div className="relative aspect-[16/9] md:aspect-[16/4] lg:aspect-auto bg-gray-200 flex items-center justify-center sm:mx-auto sm:px-4 md:h-42 lg:h-72 lg:w-full">
            <Image
              src="/partners/banner-somit-desktop-no-cta.jpg"
              className="hidden md:block object-center object-cover sm:rounded-lg shadow-lg"
              alt={"Banner de Somit aseguradora"}
              fill
              style={{
                objectFit: "cover",
              }}
            />
            <Image
              src="/partners/banner-somit-tablet-no-cta.jpg"
              className="block md:hidden object-center object-cover sm:rounded-lg shadow-lg"
              alt={"Banner de Somit aseguradora"}
              fill
              style={{
                objectFit: "cover",
              }}
            />
          </div>
        </div>
        <div className="max-w-7xl mx-auto pt-8 pb-6 px-4 ">
          <div className="">
            {success ? (
              <div className="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
                <div className="mx-auto max-w-2xl text-center">
                  <h2 className="text-balance text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
                    Tu solicitud para cotizar tu seguro vehicular ha sido
                    enviada con éxito.
                  </h2>
                  <p className="mx-auto mt-6 max-w-xl text-pretty text-lg/8 text-gray-600">
                    Pronto te contactará un agente de Somit.
                  </p>
                  <div className="mt-10 flex items-center justify-center gap-x-6">
                    {/* <a
                      href="#"
                      className="rounded-md bg-orange-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
                    >
                      Get started
                    </a> */}
                    <Link href="/">
                      <span className="text-sm/6 font-semibold text-orange-500 hover:text-orange-400">
                        Regresar <span aria-hidden="true">→</span>
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <div className="">
                <div className="mx-auto max-w-2xl text-center">
                  <h2 className="text-balance text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
                    Cotizá aquí tu seguro vehicular con Somit
                  </h2>
                  <p className="mx-auto mt-6 max-w-xl text-pretty text-lg/8 text-gray-600">
                    Dejanos tus datos para que un agente de Somit se comunique
                    con vos.
                  </p>
                </div>
                <FormProvider {...methods}>
                  <form
                    className="bg-white mt-6 w-full mx-auto"
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-y-4 gap-x-2">
                      <div className="mt-1 col-span-1 sm:col-span-6">
                        <label
                          htmlFor="firstName"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Nombre
                        </label>
                        <div className="mt-1">
                          <input
                            {...register("firstName", {
                              required: true,
                              maxLength: 20,
                            })}
                            type="text"
                            autoComplete="off"
                            className={classNames(
                              inputClass(errors.firstName, null, saving),
                              "disabled:bg-gray-100"
                            )}
                            disabled={saving}
                          />
                        </div>
                      </div>
                      <div className="mt-1 col-span-1 sm:col-span-6">
                        <label
                          htmlFor="lastNames"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Apellidos
                        </label>
                        <div className="mt-1">
                          <input
                            {...register("lastName", {
                              required: true,
                              maxLength: 20,
                            })}
                            type="text"
                            autoComplete="off"
                            className={classNames(
                              inputClass(errors.lastName, null, saving),
                              "disabled:bg-gray-100"
                            )}
                            disabled={saving}
                          />
                        </div>
                      </div>
                      <div className="mt-1 col-span-1 sm:col-span-6">
                        <label
                          htmlFor="primaryPhone"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Teléfono / Celular
                        </label>
                        <Controller
                          name="primaryPhone"
                          control={control}
                          rules={{
                            validate: (value) =>
                              isValidPhoneNumber(value || ""),
                          }}
                          render={({ field: { onChange, value } }) => (
                            <PhoneInput
                              value={value}
                              onChange={onChange}
                              defaultCountry="CR"
                              id="primaryPhone"
                              className={`${
                                errors.primaryPhone
                                  ? "ring-2 ring-red-500 ring-offset-2 ring-offset-white"
                                  : ""
                              } phone-number-input`}
                              disabled={saving}
                            />
                          )}
                        />
                        {errors["primaryPhone"] && (
                          <span className="mt-1 text-sm flex gap-x-1 text-red-500 font-medium items-center">
                            <ExclamationCircleIcon className="h-5 w-5 text-red-500 " />
                            El número de teléfono es inválido para el país
                            seleccionado.
                          </span>
                        )}
                      </div>
                      <div className="mt-1 col-span-1 sm:col-span-6">
                        <label
                          htmlFor="idNumber"
                          className="flex gap-x-1 items-center text-sm font-medium text-gray-700"
                        >
                          Número de identificación
                        </label>
                        <div className="mt-1">
                          <input
                            {...register("idNumber", {
                              required: true,
                              maxLength: 20,
                            })}
                            type="text"
                            autoComplete="off"
                            className={classNames(
                              inputClass(errors.idNumber, null, saving),
                              "disabled:bg-gray-100"
                            )}
                            disabled={saving}
                          />
                        </div>
                      </div>
                      <div className="mt-1 col-span-1 sm:col-span-12">
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Correo electrónico
                        </label>
                        <div className="mt-1">
                          <input
                            {...register("email", {
                              required: true,
                            })}
                            type="text"
                            autoComplete="off"
                            className={inputClass(errors.email, null, saving)}
                            disabled={saving}
                          />
                        </div>
                      </div>
                      <div className="mt-6 col-span-1 sm:col-span-12">
                        <div className="relative">
                          <div
                            aria-hidden="true"
                            className="absolute inset-0 flex items-center"
                          >
                            <div className="w-full border-t border-gray-300" />
                          </div>
                          <div className="relative flex justify-start">
                            <span className="bg-white pr-2 text-sm text-gray-500">
                              {" "}
                              Detalles del vehículo
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1 sm:col-span-6">
                        <label
                          htmlFor="licensePlate"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Número de placa (sin guiones)
                        </label>
                        <div className="mt-1">
                          <input
                            {...register("licensePlate", {
                              required: true,
                              pattern: {
                                // Accepts either 6 digits or 3 letters followed by 3 numbers
                                value: /^(\d{6}|[A-Za-z]{3}\d{3})$/,
                                message: "Formato de placa inválido",
                              },
                            })}
                            type="text"
                            autoComplete="off"
                            maxLength={6}
                            className={inputClass(
                              errors.licensePlate,
                              null,
                              saving
                            )}
                            disabled={saving}
                          />
                        </div>
                        {errors.licensePlate && (
                          <span className="mt-1 text-sm flex gap-x-1 text-red-500 font-medium items-center">
                            <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                            Formato inválido. Use 6 dígitos o 3 letras seguidas
                            de 3 números (ABC123)
                          </span>
                        )}
                      </div>
                      <div className="col-span-1 sm:col-span-6">
                        <Controller
                          name="brand"
                          rules={{ required: true }}
                          control={control}
                          render={({ field }) => (
                            <BrandSelect {...field} disabled={saving} />
                          )}
                        />
                      </div>
                      <div className="col-span-1 sm:col-span-6">
                        <div>
                          <label
                            htmlFor="model"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Modelo
                          </label>
                          <div className="mt-1">
                            <input
                              {...register("model", {
                                required: true,
                                maxLength: 30,
                              })}
                              type="text"
                              autoComplete="off"
                              className={inputClass(errors.model, null, saving)}
                              disabled={saving}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1 sm:col-span-6">
                        <Controller
                          name="year"
                          rules={{
                            required: true,
                            pattern: {
                              value: /^(0|[1-9]\d*)(\.\d+)?$/,
                              message: "Only numbers allowed",
                            },
                          }}
                          control={control}
                          render={({ field }) => (
                            <YearSelect {...field} disabled={saving} />
                          )}
                        />
                      </div>
                      <div className="col-span-1 sm:col-span-6">
                        <div>
                          <label
                            htmlFor="model"
                            className="flex justify-between text-sm font-medium text-gray-700"
                          >
                            Valor aproximado
                            {suggestedPrice ? (
                              <div>
                                <span>
                                  <span
                                    className="text-sm/6 font-semibold text-orange-500 hover:text-orange-400 cursor-pointer"
                                    onClick={setCarValue}
                                  >
                                    Usar valor sugerido{" "}
                                    <span aria-hidden="true">→</span>
                                  </span>{" "}
                                  {formatPrice(
                                    suggestedPrice,
                                    CURRENCY_KEYS.CRC
                                  )}
                                </span>
                              </div>
                            ) : (
                              ""
                            )}
                          </label>
                          <div className="mt-1">
                            <input
                              {...register("price", {
                                required: true,
                                maxLength: 20,
                                valueAsNumber: true,
                                pattern: {
                                  value: /^(0|[1-9]\d*)(\.\d+)?$/,
                                  message: "only numbers",
                                },
                              })}
                              type="number"
                              autoComplete="off"
                              className={inputClass(
                                errors.price,
                                "text-input",
                                saving
                              )}
                              disabled={saving}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 col-span-1 sm:col-span-12">
                        <div className="relative">
                          <div
                            aria-hidden="true"
                            className="absolute inset-0 flex items-center"
                          >
                            <div className="w-full border-t border-gray-300" />
                          </div>
                          <div className="relative flex justify-start">
                            <span className="bg-white pr-2 text-sm text-gray-500">
                              {" "}
                              Detalles del seguro
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1 sm:col-span-6">
                        <Controller
                          name="insuranceCompany"
                          rules={{
                            required: true,
                          }}
                          control={control}
                          render={({ field }) => (
                            <InsuranceCompanySelect
                              {...field}
                              disabled={saving}
                            />
                          )}
                        />
                      </div>
                      <div className="col-span-1 sm:col-span-6">
                        <Controller
                          name="frequency"
                          rules={{
                            required: true,
                          }}
                          control={control}
                          render={({ field }) => (
                            <InsuranceFrequencySelect
                              {...field}
                              disabled={saving}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <button
                      id="new-user-submit-button"
                      type="submit"
                      className="mt-6 flex w-full justify-center rounded-md border border-transparent bg-orange-500 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                      disabled={saving}
                    >
                      Enviar
                    </button>
                  </form>
                </FormProvider>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

Pricing.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};
